import type { Site } from "../../types/scene";
import { STANDARD_FLOOR_HFIELD_NAME } from "../../utils/terrainFloor";
import { VIEWPORT_EXPORT_CAMERA_NAME } from "./xmlGeneratorConfig";
import { emitFixedCamerasXml, emitWorldbodySitesXml } from "./xmlGeneratorCameraSite";
import { emitFixedLightsXml } from "./xmlGeneratorLight";
import { sanitizeMjcfName } from "../../utils/mjcfNames";
import type { ExportBodyNode, ExportCamera, ExportLight } from "./xmlGenerationTypes";

type CameraSiteXmlHelpers = {
  arrayToString: (values: Array<number | string>) => string;
  colorToRgba: (color: unknown) => [number, number, number, number] | null;
  findSiteByIdentifier: (identifier: string | undefined | null) => Site | null;
  normalizeQuat: (values: unknown) => number[];
  quatMultiply: (a: number[], b: number[]) => number[];
  quatRotateVec: (qRaw: number[], vRaw: number[]) => number[];
  sanitizeMjcfName: (value: string) => string;
  toMjcfQuatWxyz: (values: unknown) => number[];
  viewerQuatToMujoco: (qRaw: number[]) => number[];
  viewerVecToMujoco: (vRaw: number[]) => number[];
  xmlEscapeAttr: (value: string) => string;
};

export const emitWorldbodyXml = ({
  bodyTreeForExport,
  cameraExportNameByUuid,
  cameraSiteXmlHelpers,
  cameraValuesForExport,
  lightExportNameByUuid,
  lightValuesForExport,
  hfieldNameToXmlName,
  materialUuidToName,
  siteExportNameByUuid,
  siteMap,
  standardFloorHField,
  bodyUuidToXmlName,
  worldbodyBodyXml,
}: {
  bodyTreeForExport: ExportBodyNode[];
  cameraExportNameByUuid: Record<string, string>;
  cameraSiteXmlHelpers: CameraSiteXmlHelpers;
  cameraValuesForExport: ExportCamera[];
  lightExportNameByUuid: Record<string, string>;
  lightValuesForExport: ExportLight[];
  hfieldNameToXmlName: Record<string, string>;
  materialUuidToName: Record<string, string>;
  siteExportNameByUuid: Record<string, string>;
  siteMap: Record<string, Site>;
  standardFloorHField: unknown;
  bodyUuidToXmlName: Record<string, string>;
  worldbodyBodyXml: string;
}): string => {
  let worldbodyXml = "  <worldbody>\n";
  if (lightValuesForExport.length === 0) {
    worldbodyXml += '    <light name="spotlight" pos="0 0 2" dir="0 0 -1"/>\n';
  }
  if (standardFloorHField) {
    const hfieldXmlName =
      hfieldNameToXmlName[STANDARD_FLOOR_HFIELD_NAME] ||
      sanitizeMjcfName(STANDARD_FLOOR_HFIELD_NAME);
    worldbodyXml += `    <geom name="floor" type="hfield" hfield="${hfieldXmlName}" pos="0 0 0" contype="1" conaffinity="15" condim="3" group="3" friction="2 0.02 0.001"/>\n`;
  } else {
    worldbodyXml +=
      '    <geom name="floor" type="plane" size="20 20 0.1" pos="0 0 0" contype="1" conaffinity="15" condim="3" group="3" friction="2 0.02 0.001"/>\n';
  }
  worldbodyXml += emitFixedCamerasXml({
    cameraValuesForExport,
    cameraExportNameByUuid,
    viewportExportCameraName: VIEWPORT_EXPORT_CAMERA_NAME,
    bodyUuidToXmlName,
    helpers: cameraSiteXmlHelpers,
  });

  worldbodyXml += emitFixedLightsXml({
    lightValuesForExport,
    lightExportNameByUuid,
    bodyUuidToXmlName,
    helpers: cameraSiteXmlHelpers,
  });

  worldbodyXml += emitWorldbodySitesXml({
    bodyTree: bodyTreeForExport,
    siteMap,
    siteExportNameByUuid,
    materialUuidToName,
    helpers: cameraSiteXmlHelpers,
  });

  worldbodyXml += worldbodyBodyXml;
  worldbodyXml += "  </worldbody>\n\n";
  return worldbodyXml;
};
